import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Sparkles, X, Loader2 } from 'lucide-react';

// --- Styled Components (Giữ nguyên phần giao diện đẹp của bạn) ---
const SectionWrapper = styled.section`
  padding: 5rem 0;
  background: white;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1rem;
  @media (min-width: 640px) { font-size: 3rem; }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #4b5563;
  line-height: 1.6;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  @media (min-width: 768px) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 1024px) { gap: 1.5rem; }
`;

const GalleryItem = styled.div`
  position: relative;
  background: #f3f4f6;
  border-radius: 1rem;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  animation: scaleIn 0.5s ease-out backwards;
  animation-delay: ${props => props.$delay || '0s'};

  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.8); }
    to { opacity: 1; transform: scale(1); }
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  ${GalleryItem}:hover & { transform: scale(1.1); }
`;

const GalleryItemInner = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0);
  transition: background 0.3s ease;
  &:hover { background: rgba(0, 0, 0, 0.3); }
`;

const GalleryIcon = styled.div`
  position: relative;
  z-index: 1;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  svg { width: 40px; height: 40px; color: white; filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2)); }
  ${GalleryItem}:hover & { opacity: 1; transform: translateY(0); }
`;

const GalleryLabel = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  padding: 1.5rem 1rem 1rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  color: white;
  font-weight: 600;
  font-size: 1rem;
  text-align: center;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  ${GalleryItem}:hover & { transform: translateY(0); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 999;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 1rem;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  img { width: 100%; height: auto; display: block; max-height: 90vh; object-fit: contain; background: #000; }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem; right: 1rem;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  width: 44px; height: 44px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; z-index: 10;
  svg { color: white; }
`;

// --- Component Chính ---
const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // URL API từ Worker của bạn
  const API_URL = "https://nk-salon-hair.germibays.workers.dev/api/gallery";

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(API_URL);
        const data = await response.json();
        // Giả sử data trả về là mảng [{id, src, label}, ...]
        setGalleryItems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Lỗi tải ảnh từ server:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const openModal = (item) => setSelectedImage(item);
  const closeModal = () => setSelectedImage(null);

  return (
    <>
      <SectionWrapper id="gallery">
        <Container>
          <SectionHeader>
            <SectionTitle>KM Salon Mẫu Nổi Bật</SectionTitle>
            <SectionSubtitle>
              Môi trường sang trọng, tiện nghi hiện đại
            </SectionSubtitle>
          </SectionHeader>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
            </div>
          ) : (
            <GalleryGrid>
              {galleryItems.map((item, index) => (
                <GalleryItem
                  key={item.id || index}
                  $delay={`${(index % 6) * 0.1}s`}
                  onClick={() => openModal(item)}
                >
                  <StyledImage
                    src={item.src}
                    alt={item.label}
                    loading="lazy"
                  />
                  <GalleryItemInner>
                    <GalleryIcon>
                      <Sparkles />
                    </GalleryIcon>
                    <GalleryLabel>{item.label}</GalleryLabel>
                  </GalleryItemInner>
                </GalleryItem>
              ))}
            </GalleryGrid>
          )}

          {!loading && galleryItems.length === 0 && (
            <p style={{ textAlign: 'center', color: '#999' }}>Chưa có ảnh nào trong thư viện.</p>
          )}
        </Container>
      </SectionWrapper>

      {/* Modal hiển thị ảnh phóng to */}
      <ModalOverlay $isOpen={selectedImage !== null} onClick={closeModal}>
        {selectedImage && (
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>
              <X size={24} />
            </CloseButton>
            <img src={selectedImage.src} alt={selectedImage.label} />
            <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold' }}>
              {selectedImage.label}
            </div>
          </ModalContent>
        )}
      </ModalOverlay>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default GallerySection;